import React, { useState } from 'react';
import styled from 'styled-components';
import GemIcon from './icons/GemIcon';
import { PointType } from '../types/pointTypeManagement';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PointTypeBlockProps {
  pointType: PointType;
  onEdit: (pointType: PointType) => void;
  onDelete: (pointTypeId: string) => void;
  isEditing?: boolean;
  onSave?: (pointType: PointType) => void;
  onCancel?: () => void;
}

const PointTypeBlock: React.FC<PointTypeBlockProps> = ({
  pointType,
  onEdit,
  onDelete,
  isEditing = false,
  onSave,
  onCancel
}) => {
  const [editData, setEditData] = useState({
    points: pointType.points,
    memo: pointType.memo
  });

  // useSortable은 항상 호출되어야 함 (React Hooks 규칙)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pointType.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        ...pointType,
        ...editData
      });
    }
  };

  const handleCancel = () => {
    setEditData({
      points: pointType.points,
      memo: pointType.memo
    });
    if (onCancel) {
      onCancel();
    }
  };

  if (isEditing) {
    return (
      <EditingBlock>
        <EditingRow>
          <IconContainer>
            <GemIcon color={pointType.category === 'positive' ? 'red' : 'gray'} size={24} />
          </IconContainer>
          <EditingInput
            value={editData.points}
            onChange={(e) => setEditData(prev => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
            type="number"
            min="0"
            placeholder="포인트"
          />
          <EditingTextArea
            value={editData.memo}
            onChange={(e) => setEditData(prev => ({ ...prev, memo: e.target.value }))}
            placeholder="메모를 입력하세요"
          />
        </EditingRow>
        <EditingActions>
          <ActionButton data-variant="modal-secondary" onClick={handleCancel}>닫기</ActionButton>
          <ActionButton onClick={handleSave} $primary>확인</ActionButton>
        </EditingActions>
      </EditingBlock>
    );
  }

  return (
    <Block ref={setNodeRef} style={style}>
      <BlockContent>
        <IconContainer>
          <GemIcon color={pointType.category === 'positive' ? 'red' : 'gray'} size={20} />
        </IconContainer>
        <PointsText>{pointType.points}점</PointsText>
        <MemoText>{pointType.memo}</MemoText>
      </BlockContent>
      <ActionButtons>
        <EditButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit(pointType);
          }}
          title="수정"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </EditButton>
        <DeleteButton
          onClick={(e) => {
            e.stopPropagation();
            onDelete(pointType.id);
          }}
          title="삭제"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </DeleteButton>
        <DragHandle
          {...attributes}
          {...listeners}
          aria-label="포인트 세트 순서 조정"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
        </DragHandle>
      </ActionButtons>
    </Block>
  );
};

// Styled Components
const Block = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  min-height: 60px;
  max-height: 80px;
  position: relative;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    border-color: rgba(var(--color-accent-rgb), 0.3);
  }

  @media (max-width: 480px) {
    padding: 4px 6px 4px 10px;
    min-height: 50px;
    max-height: 70px;
  }

  @media (max-width: 360px) {
    padding: 2px 4px 2px 8px;
    min-height: 48px;
    max-height: 65px;
  }
`;

const BlockContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    gap: 8px;
  }

  @media (max-width: 360px) {
    gap: 6px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PointsText = styled.span`
  font-weight: 600;
  color: var(--color-accent-text);
  font-size: 14px;
  min-width: 50px;
  text-align: center;

  @media (max-width: 480px) {
    min-width: 40px;
    font-size: 13px;
  }

  @media (max-width: 360px) {
    min-width: 35px;
    font-size: 12px;
  }
`;

const MemoText = styled.span`
  color: var(--color-text-primary);
  font-size: 14px;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;

  @media (max-width: 480px) {
    font-size: 13px;
  }

  @media (max-width: 360px) {
    font-size: 12px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    gap: 2px;
  }

  @media (max-width: 360px) {
    gap: 1px;
  }
`;

const DragHandle = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: var(--color-panel);
    color: var(--color-accent-strong);
  }

  &:active {
    cursor: grabbing;
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const EditButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: var(--color-panel);
    color: #007bff;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const DeleteButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: var(--color-panel);
    color: #dc3545;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const EditingBlock = styled.div`
  background: var(--color-surface);
  border: 2px solid rgba(var(--color-accent-rgb), 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EditingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const EditingInput = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  width: 55px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: rgba(var(--color-accent-rgb), 0.5);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }

  /* 숫자 입력 화살표 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const EditingTextArea = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  flex: 1;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: rgba(var(--color-accent-rgb), 0.5);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
`;

const EditingActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  padding: 6px 16px;
  border: 1px solid ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 6px;
  background: ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$primary ? 'white' : 'var(--color-text-primary)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$primary ? 'rgba(var(--color-accent-rgb), 0.9)' : 'var(--color-panel)'};
  }
`;

export default PointTypeBlock;
